import {
  EmojiEmotions,
  FormatListBulleted,
  Image,
  Mic,
  PictureAsPdf,
  Send,
  SmartToy,
} from "@mui/icons-material";
import {
  Box,
  CircularProgress,
  IconButton,
  InputBase,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Skeleton,
  styled,
  Tooltip,
  Typography,
} from "@mui/material";
import { keyframes } from "@mui/system";
import { addHours, isAfter } from "date-fns";
import EmojiPicker from "emoji-picker-react";
import React, {
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useAccount, useChat, useToast } from "../../../context";
import { useAIControl, useMessageHandler } from "../../../hooks";
import { useApiError } from "../../../hooks/useApiError";
import {
  CampaignTemplate,
  ChatInputProps,
  ChatInputState,
  IErrorResponse,
  MessageData,
} from "../../../interfaces/all";
import {
  getTemplateParameters,
  newMessages,
  sendTemplateMessage,
  getTemplates,
  getCampaignsWithTemplates,
} from "../../../services/api";
import { apiService } from "../../../services/axios";
import { ParameterConfig } from "../../../types/template-config";
import TemplateSelector from "./TemplateSelector";

const Container = styled(Box)`
  background: #f0f2f5;
  padding: 8px 6px;
  display: flex;
  align-items: center;
  gap: 8px;
  border-top: 1px solid #e9edef;
  position: relative;

  @media (max-width: 600px) {
    padding: 6px 4px;
    gap: 6px;
  }
`;

const InputContainer = styled(Box)`
  flex: 1;
  background: #fff;
  border-radius: 8px;
  padding: 8px 6px;
  display: flex;
  align-items: center;
  gap: 8px;
  min-height: 24px;
  max-height: 100px;
  overflow-y: auto;

  @media (max-width: 600px) {
    border-radius: 24px; /* Border-radius mais moderno */
    padding: 12px 16px; /* Padding aumentado para melhor toque */
    min-height: 48px; /* Altura mínima adequada para mobile */
    max-height: 120px;
    gap: 8px; /* Gap aumentado */
  }
`;

const StyledInput = styled(InputBase)({
  flex: 1,
  fontSize: "15px",
  lineHeight: "20px",
  color: "#111b21",
  "& .MuiInputBase-input": {
    padding: "0",
    height: "auto",
    minHeight: "24px",
    maxHeight: "100px",
    overflowY: "auto",
  },
  "&::placeholder": {
    color: "#667781",
  },
  "@media (max-width: 600px)": {
    fontSize: "16px" /* Prevenir zoom no iOS */,
    lineHeight: "24px" /* Line height otimizado */,
    "& .MuiInputBase-input": {
      minHeight: "24px" /* Altura mínima ajustada */,
      maxHeight: "120px",
    },
  },
});

const pulseAnimation = keyframes`
    0% {
        transform: scale(1);
    }
    50% {
        transform: scale(1.1);
    }
    100% {
        transform: scale(1);
    }
`;

const IconButtonStyled = styled(IconButton)<{ "data-is-updating"?: boolean }>(
  ({ "data-is-updating": isUpdating }) => ({
    color: "#54656f",
    padding: 8,
    position: "relative",
    "&:hover": {
      backgroundColor: "rgba(0, 0, 0, 0.05)",
    },
    ...(isUpdating && {
      animation: `${pulseAnimation} 1s infinite`,
      pointerEvents: "none",
    }),
    "@media (max-width: 600px)": {
      padding: 12 /* Padding aumentado para touch target */,
      "& svg": {
        fontSize: "24px" /* Ícone maior para melhor visibilidade */,
      },
    },
  }),
);

const MicButton = styled(IconButton)({
  backgroundColor: "#54656f",
  color: "#fff",
  padding: 8,
  borderRadius: "50%",
  transition: "all 0.3s ease" /* Transição mais suave */,
  "&:hover": {
    backgroundColor: "#4a5568",
    transform: "scale(1.05)",
  },
  "&:active": {
    transform: "scale(0.95)",
  },
  "&:disabled": {
    backgroundColor: "#e9edef",
    color: "#667781",
  },
  "@media (max-width: 600px)": {
    padding: 12 /* Padding aumentado para touch target */,
    "& svg": {
      fontSize: "24px" /* Ícone maior para melhor visibilidade */,
    },
  },
});

const SendButton = styled(IconButton)<{ "data-has-text"?: boolean }>(
  ({ "data-has-text": hasText }) => ({
    backgroundColor: hasText ? "#008069" : "#54656f",
    color: "#fff",
    padding: 8,
    borderRadius: "50%",
    transition: "all 0.3s ease" /* Transição mais suave */,
    "&:hover": {
      backgroundColor: hasText ? "#006b5a" : "#4a5568",
      transform: "scale(1.05)",
    },
    "&:active": {
      transform: "scale(0.95)",
    },
    "@media (max-width: 600px)": {
      padding: 12 /* Padding aumentado para touch target */,
      "& svg": {
        fontSize: "24px" /* Ícone maior para melhor visibilidade */,
      },
    },
  }),
);

const LoadingOverlay = styled(Box)({
  position: "absolute",
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  backgroundColor: "rgba(255, 255, 255, 0.7)",
  borderRadius: "50%",
});

const EmojiPickerContainer = styled(Box)`
  position: absolute;
  bottom: calc(100% + 8px);
  left: 0;
  z-index: 1000;
  box-shadow:
    0 2px 5px rgba(11, 20, 26, 0.08),
    0 1px 2px rgba(11, 20, 26, 0.12);
  border-radius: 12px;
  background: #fff;
  overflow: hidden;

  .emoji-mart {
    border: none !important;
    border-radius: 12px !important;
    box-shadow: none !important;
  }

  .emoji-mart-search {
    padding: 8px !important;
  }

  .emoji-mart-search input {
    background: #f0f2f5 !important;
    border: none !important;
    border-radius: 8px !important;
    padding: 8px 12px !important;
    font-size: 14px !important;
    color: #111b21 !important;
  }

  .emoji-mart-category-label span {
    background: #fff !important;
    color: #667781 !important;
    font-size: 13px !important;
    font-weight: 500 !important;
  }

  .emoji-mart-anchor {
    color: #54656f !important;
    padding: 8px !important;
  }

  .emoji-mart-anchor:hover,
  .emoji-mart-anchor-selected {
    color: #008069 !important;
  }

  .emoji-mart-scroll {
    border-top: 1px solid #e9edef !important;
  }

  .emoji-mart-emoji {
    padding: 6px !important;
  }

  .emoji-mart-emoji:hover {
    background: #f0f2f5 !important;
  }
`;

const FileInput = styled("input")`
  display: none;
`;

const PreviewContainer = styled(Box)`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 8px;
  padding: 0 8px;
`;

const PreviewItem = styled(Box)`
  position: relative;
  width: 100px;
  height: 100px;
  border-radius: 8px;
  overflow: hidden;
  background: #f0f2f5;
`;

const PreviewImage = styled("img")`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const RemoveButton = styled(IconButton)`
  position: absolute;
  top: 4px;
  right: 4px;
  background: rgba(0, 0, 0, 0.5);
  color: #fff;
  padding: 4px;

  &:hover {
    background: rgba(0, 0, 0, 0.7);
  }
`;

const SlashMenuContainer = styled(Box)`
  position: absolute;
  bottom: calc(100% + 8px);
  left: 8px;
  right: 8px;
  background: rgba(255, 255, 255, 0.9);
  backdrop-filter: blur(12px);
  border: 1px solid rgba(255, 255, 255, 0.6);
  border-radius: 12px;
  box-shadow: 0 8px 32px rgba(11, 20, 26, 0.15);
  max-height: 250px;
  overflow-y: auto;
  z-index: 1100;
  padding: 8px 0;
  display: flex;
  flex-direction: column;
`;

const SlashMenuItem = styled(Box)<{ selected: boolean }>(({ selected }) => ({
  padding: "10px 16px",
  display: "flex",
  alignItems: "center",
  gap: "12px",
  cursor: "pointer",
  backgroundColor: selected ? "rgba(0, 128, 105, 0.08)" : "transparent",
  borderLeft: selected ? "4px solid #008069" : "4px solid transparent",
  transition: "all 0.15s ease",
  "&:hover": {
    backgroundColor: "rgba(0, 128, 105, 0.04)",
  },
}));

const ChatInput: React.FC<ChatInputProps> = memo(({ onSendMessage }) => {
  const [loading, setLoading] = useState(false);
  const [state, setState] = useState<ChatInputState>({
    inputValue: "",
    isUploading: false,
    showEmojiPicker: false,
    showFileMenu: false,
    fileAnchorEl: null,
    previewFiles: [],
    previewUrls: [],
    disableAI: false,
  });

  const [showTemplateSelector, setShowTemplateSelector] = useState(false);
  const [sendingTemplate, setSendingTemplate] = useState(false);
  const [allTemplates, setAllTemplates] = useState<any[]>([]);
  const [campaignTemplates, setCampaignTemplates] = useState<
    CampaignTemplate[]
  >([]);
  const [showSlashMenu, setShowSlashMenu] = useState(false);
  const [slashSearch, setSlashSearch] = useState("");
  const [selectedSlashIndex, setSelectedSlashIndex] = useState(0);
  const [initialTemplateId, setInitialTemplateId] = useState<
    string | undefined
  >(undefined);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { selectedConversation } = useChat();
  const { account, socket } = useAccount();
  const { showError, showSuccess } = useToast();
  const { handleError, handleApiCall } = useApiError();

  const validPhoneNumber = useMemo(
    () =>
      selectedConversation?.valid_telephone?.replace(/\D/g, "") ||
      selectedConversation?.telephone?.replace(/\D/g, "") ||
      "",
    [selectedConversation?.valid_telephone, selectedConversation?.telephone],
  );

  const { aiState, toggleAI, isCompanyAIEnabled } =
    useAIControl(validPhoneNumber);

  const { messages } = useMessageHandler({
    socket,
    selectedConversation,
  });

  const isInputDisabled = useMemo(() => {
    if (!selectedConversation) return true;

    if (messages.length === 0) return false;

    const lastClientMessage = [...messages]
      .reverse()
      .find((msg) => msg.sentBy === "client");

    if (!lastClientMessage) return false;

    const lastMessageTime = new Date(lastClientMessage.timestamp);
    const now = new Date();
    const timeLimit = addHours(lastMessageTime, 24);

    return isAfter(now, timeLimit);
  }, [messages, selectedConversation]);

  // Preload templates for quick slash commands
  useEffect(() => {
    const preloadTemplates = async () => {
      if (!account?.company_id) return;
      try {
        const [templatesRes, campaignsRes] = await Promise.all([
          getTemplates(account.company_id),
          getCampaignsWithTemplates(),
        ]);
        if (templatesRes.status === "success" && templatesRes.data?.data) {
          setAllTemplates(templatesRes.data.data as any[]);
        }
        if (campaignsRes.status === "success" && campaignsRes.data) {
          setCampaignTemplates(campaignsRes.data as CampaignTemplate[]);
        }
      } catch (err) {
        console.error(
          "Erro ao pré-carregar templates para o menu de comandos:",
          err,
        );
      }
    };
    preloadTemplates();
  }, [account?.company_id]);

  // Filter templates based on campaign and search query
  const filteredSlashTemplates = useMemo(() => {
    if (!selectedConversation?.campaign_id) return [];
    const activeCampaignTemplates = campaignTemplates.filter(
      (ct) =>
        String(ct.campaign_id) === String(selectedConversation.campaign_id),
    );

    return activeCampaignTemplates
      .map((ct) => {
        const matchedTemplate = allTemplates.find(
          (t) => t.id === ct.template_id,
        );
        return matchedTemplate
          ? { ...ct, templateDetails: matchedTemplate }
          : null;
      })
      .filter((t): t is any => {
        if (!t || !t.templateDetails) return false;
        if (!slashSearch) return true;
        const nameMatch = t.templateDetails.name
          .toLowerCase()
          .includes(slashSearch.toLowerCase());
        const categoryMatch = (t.templateDetails.category || "")
          .toLowerCase()
          .includes(slashSearch.toLowerCase());
        return nameMatch || categoryMatch;
      });
  }, [
    selectedConversation?.campaign_id,
    campaignTemplates,
    allTemplates,
    slashSearch,
  ]);

  // Handle template selection from quick slash commands
  const handleSelectSlashTemplate = useCallback(
    async (campaignTemplate: CampaignTemplate & { templateDetails?: any }) => {
      if (!selectedConversation || !account) {
        showError({
          message: "Conversa ou usuário não encontrado",
          status: "error",
        } as IErrorResponse);
        return;
      }

      const templateDetails = campaignTemplate.templateDetails;
      if (!templateDetails) return;

      setShowSlashMenu(false);
      setState((prev) => ({ ...prev, inputValue: "" })); // Clear command character
      setSendingTemplate(true);

      try {
        const response = await getTemplateParameters(
          templateDetails.id,
          templateDetails.name,
          account.company_id,
        );

        if (response.status === "success" && response.data) {
          const templateData = response.data as any;
          const reqCount = templateData.requiredParametersCount || 0;

          // Load predefined configuration
          let predefinedConfig: any[] | null = null;
          try {
            const configRes = await apiService.get(
              `/template-configs/${templateDetails.id}?companyId=${account.company_id}`,
            );
            if (
              configRes.status === "success" &&
              configRes.data &&
              typeof configRes.data === "object" &&
              configRes.data !== null &&
              "parameters" in configRes.data
            ) {
              predefinedConfig = (configRes.data as any).parameters || [];
            }
          } catch (e) {
            // Ignore config loading errors
          }

          const realValues = {
            clientName:
              selectedConversation.valid_name ||
              selectedConversation.name ||
              "",
            clientPhone:
              selectedConversation.valid_telephone ||
              selectedConversation.telephone ||
              "",
            companyName: account.company?.name || "",
            attendantName:
              selectedConversation.attendant_name || account.name || "",
          };

          let canAutoSend = true;
          const parameters: string[] = [];

          if (predefinedConfig && predefinedConfig.length > 0) {
            for (let i = 0; i < predefinedConfig.length; i++) {
              const param = predefinedConfig[i];
              let type: "fixed" | "dynamic" = "dynamic";
              let value = "";
              let dynamicField = "clientName";

              if (typeof param === "object" && param !== null) {
                type = param.type || "dynamic";
                value = param.value || "";
                dynamicField = param.dynamicField || "clientName";
              } else {
                type = "fixed";
                value = String(param);
              }

              if (type === "dynamic") {
                if (dynamicField === "scheduledDate") {
                  canAutoSend = false;
                  break;
                }
                switch (dynamicField) {
                  case "clientName":
                    value = realValues.clientName;
                    break;
                  case "clientPhone":
                    value = realValues.clientPhone;
                    break;
                  case "companyName":
                    value = realValues.companyName;
                    break;
                  case "attendantName":
                    value = realValues.attendantName;
                    break;
                  default:
                    value = realValues.clientName;
                }
              }
              parameters.push(value);
            }
          } else if (reqCount > 0) {
            const fieldMapping = ["clientName", "attendantName", "companyName"];
            const valueMapping = [
              realValues.clientName,
              realValues.attendantName,
              realValues.companyName,
            ];

            if (reqCount <= 3) {
              for (let i = 0; i < reqCount; i++) {
                parameters.push(valueMapping[i] || "");
              }
            } else {
              canAutoSend = false;
            }
          }

          if (canAutoSend) {
            let imageUrl: string | undefined = undefined;
            if (templateData.components) {
              const headerComponent = templateData.components.find(
                (c: any) => c.type === "HEADER" && c.format === "IMAGE",
              );
              if (headerComponent) {
                imageUrl =
                  headerComponent.example?.header_handle?.[0] ||
                  headerComponent.example?.header_url?.[0] ||
                  headerComponent.example?.header_url ||
                  "https://via.placeholder.com/800x600/008069/ffffff?text=Template+Image";
              }
            }

            const sendResult = await sendTemplateMessage({
              to:
                selectedConversation.valid_telephone ||
                selectedConversation.telephone,
              templateId: templateDetails.id,
              templateName: templateDetails.name,
              templateLanguage: templateDetails.language || "pt_BR",
              userId: account.id,
              companyId: account.company_id,
              imageUrl: imageUrl,
              parameters: parameters,
              leadId: (selectedConversation as any).id,
              campaignId: (selectedConversation as any).campaign_id,
              leadSource:
                (selectedConversation as any).lead_source || "leads_lead",
            } as any);

            if (sendResult.status === "success") {
              onSendMessage(sendResult.data.data);
              showSuccess(
                `Template "${templateDetails.name}" enviado com sucesso!`,
              );
            } else {
              showError(sendResult as IErrorResponse);
            }
          } else {
            setInitialTemplateId(templateDetails.id);
            setShowTemplateSelector(true);
          }
        } else {
          setInitialTemplateId(templateDetails.id);
          setShowTemplateSelector(true);
        }
      } catch (e) {
        console.error("Erro ao processar seleção rápida de template:", e);
        setInitialTemplateId(templateDetails.id);
        setShowTemplateSelector(true);
      } finally {
        setSendingTemplate(false);
      }
    },
    [selectedConversation, account, onSendMessage, showError, showSuccess],
  );

  const handleFileChange = useCallback(
    async (event: React.ChangeEvent<HTMLInputElement>) => {
      const files = Array.from(event.target.files || []);
      if (files.length === 0) return;

      const newUrls = files.map((file) => URL.createObjectURL(file));

      setState((prev) => ({
        ...prev,
        isUploading: true,
        previewFiles: [...prev.previewFiles, ...files],
        previewUrls: [...prev.previewUrls, ...newUrls],
      }));

      try {
        // Implementar lógica de upload aqui
        setState((prev) => ({ ...prev, isUploading: false }));
      } catch (error) {
        handleError(error, {
          fallbackMessage: "Erro ao processar arquivo. Tente novamente.",
          onError: () => {
            setState((prev) => ({ ...prev, isUploading: false }));
          },
        });
      }
    },
    [handleError],
  );

  const handleRemoveFile = useCallback((index: number) => {
    setState((prev) => {
      const newFiles = [...prev.previewFiles];
      const newUrls = [...prev.previewUrls];
      URL.revokeObjectURL(newUrls[index]);
      newFiles.splice(index, 1);
      newUrls.splice(index, 1);
      return { ...prev, previewFiles: newFiles, previewUrls: newUrls };
    });
  }, []);

  const handleSendMessage = useCallback(async () => {
    if (
      !selectedConversation ||
      !account ||
      (!state.inputValue.trim() && state.previewFiles.length === 0)
    )
      return;

    setLoading(true);

    const result = await handleApiCall(
      async () => {
        if (state.previewFiles.length > 0) {
          const file = state.previewFiles[0];
          const formData = new FormData();
          formData.append("file", file);
          formData.append(
            "to",
            selectedConversation.valid_telephone ||
              selectedConversation.telephone,
          );
          formData.append("userId", account.id);
          formData.append("companyId", account.company_id);
          if (state.inputValue.trim()) {
            formData.append(
              "content",
              JSON.stringify({
                text: state.inputValue.trim(),
              }),
            );
          }
          return await newMessages(formData);
        } else {
          const messageData: MessageData = {
            to:
              selectedConversation.valid_telephone ||
              selectedConversation.telephone,
            type: "text",
            content: {
              text: state.inputValue.trim(),
              files: [],
            },
            userId: account.id,
            companyId: account.company_id,
            disableAI: state.disableAI,
          };
          return await newMessages(messageData);
        }
      },
      {
        onSuccess: (response) => {
          if (response.status === "success") {
            onSendMessage(response.data.data);
            setState((prev) => ({
              ...prev,
              inputValue: "",
              previewFiles: [],
              previewUrls: [],
              disableAI: false,
            }));
            showSuccess("Mensagem enviada com sucesso");
          } else {
            showError(response as IErrorResponse);
          }
          // Resetar loading após sucesso ou erro na resposta
          setLoading(false);
        },
        onError: () => {
          setLoading(false);
        },
        fallbackErrorMessage: "Erro ao enviar mensagem. Tente novamente.",
        successMessage: undefined, // Não mostrar toast automático, já temos showSuccess
      },
    );

    if (!result) {
      setLoading(false);
    }
  }, [
    selectedConversation,
    account,
    state,
    onSendMessage,
    showSuccess,
    showError,
    handleApiCall,
  ]);

  const handleToggleAI = useCallback(async () => {
    // Se a IA geral da empresa estiver desativada, mostrar mensagem de erro
    if (!isCompanyAIEnabled) {
      showError({
        message:
          "A IA geral da empresa está desativada. Ative primeiro nas configurações da empresa.",
        status: "error",
      } as IErrorResponse);
      return;
    }

    await handleApiCall(
      async () => {
        return await toggleAI(
          !aiState.isActive,
          !aiState.isActive
            ? "Reativado pelo usuário"
            : "Desativado temporariamente pelo usuário",
        );
      },
      {
        onSuccess: (response) => {
          if (response?.status === "success") {
            setState((prev) => ({ ...prev, disableAI: !aiState.isActive }));
            showSuccess(
              !aiState.isActive
                ? "IA reativada com sucesso"
                : "IA desativada temporariamente",
            );
          } else if (response) {
            showError(response as IErrorResponse);
          }
        },
        fallbackErrorMessage: "Erro ao alterar estado da IA. Tente novamente.",
        successMessage: undefined, // Não mostrar toast automático, já temos showSuccess
      },
    );
  }, [
    aiState.isActive,
    toggleAI,
    showSuccess,
    showError,
    handleApiCall,
    isCompanyAIEnabled,
  ]);

  const handleEmojiSelect = useCallback((emojiObject: any) => {
    setState((prev) => ({
      ...prev,
      inputValue: prev.inputValue + emojiObject.emoji,
      showEmojiPicker: false,
    }));
  }, []);

  const handleFileMenu = useCallback(
    (
      action: "open" | "close" | "select",
      eventOrType?: React.MouseEvent<HTMLElement> | string,
    ) => {
      switch (action) {
        case "open":
          if (
            eventOrType &&
            "currentTarget" in (eventOrType as React.MouseEvent<HTMLElement>)
          ) {
            setState((prev) => ({
              ...prev,
              fileAnchorEl: (eventOrType as React.MouseEvent<HTMLElement>)
                .currentTarget,
              showFileMenu: true,
            }));
          }
          break;
        case "close":
          setState((prev) => ({
            ...prev,
            fileAnchorEl: null,
            showFileMenu: false,
          }));
          break;
        case "select":
          if (typeof eventOrType === "string" && fileInputRef.current) {
            // Remove o foco do item do menu antes de fechar
            if (document.activeElement instanceof HTMLElement) {
              document.activeElement.blur();
            }
            setState((prev) => ({
              ...prev,
              fileAnchorEl: null,
              showFileMenu: false,
            }));
            setTimeout(() => {
              if (fileInputRef.current) {
                fileInputRef.current.accept = eventOrType;
                fileInputRef.current.click();
              }
            }, 100);
          }
          break;
      }
    },
    [],
  );

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      if (showSlashMenu && filteredSlashTemplates.length > 0) {
        if (event.key === "ArrowDown") {
          event.preventDefault();
          setSelectedSlashIndex(
            (prev) => (prev + 1) % filteredSlashTemplates.length,
          );
          return;
        }
        if (event.key === "ArrowUp") {
          event.preventDefault();
          setSelectedSlashIndex(
            (prev) =>
              (prev - 1 + filteredSlashTemplates.length) %
              filteredSlashTemplates.length,
          );
          return;
        }
        if (event.key === "Enter") {
          event.preventDefault();
          const selectedTemplate = filteredSlashTemplates[selectedSlashIndex];
          if (selectedTemplate) {
            handleSelectSlashTemplate(selectedTemplate);
          }
          return;
        }
        if (event.key === "Escape") {
          event.preventDefault();
          setShowSlashMenu(false);
          return;
        }
      }

      if (event.key === "Enter" && !event.shiftKey) {
        event.preventDefault();
        handleSendMessage();
      }
    },
    [
      showSlashMenu,
      filteredSlashTemplates,
      selectedSlashIndex,
      handleSendMessage,
      handleSelectSlashTemplate,
    ],
  );

  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const val = e.target.value;
      setState((prev) => ({ ...prev, inputValue: val }));

      if (val.startsWith("/")) {
        setShowSlashMenu(true);
        setSlashSearch(val.slice(1));
        setSelectedSlashIndex(0);
      } else {
        setShowSlashMenu(false);
        setSlashSearch("");
      }
    },
    [],
  );

  const handleToggleEmojiPicker = useCallback(() => {
    setState((prev) => ({ ...prev, showEmojiPicker: !prev.showEmojiPicker }));
  }, []);

  const handleTemplateSelect = useCallback(
    async (
      template: CampaignTemplate & {
        parameterConfigs?: ParameterConfig[];
        templateParameters?: any;
      },
    ) => {
      if (!selectedConversation || !account) {
        showError({
          message: "Conversa ou usuário não encontrado",
          status: "error",
        } as IErrorResponse);
        return;
      }

      setSendingTemplate(true);

      // Obter a URL real da imagem do template se existir
      let imageUrl = undefined;

      if (
        template.templateParameters &&
        template.templateParameters.components
      ) {
        const headerComponent = template.templateParameters.components.find(
          (component: any) =>
            component.type === "HEADER" && component.format === "IMAGE",
        );

        if (headerComponent) {
          // Tentar obter a URL da imagem do template
          imageUrl =
            headerComponent.example?.header_handle?.[0] ||
            headerComponent.example?.header_url?.[0] ||
            headerComponent.example?.header_url;
        }
      } else {
        // Se templateParameters não está disponível, tentar carregar diretamente
        console.log(
          "⚠️ [TEMPLATE DEBUG] templateParameters não disponível, tentando carregar diretamente",
        );
        try {
          const response = await getTemplateParameters(
            template.template_id,
            template.template_name,
            account.company_id,
          );
          if (
            response.status === "success" &&
            response.data &&
            typeof response.data === "object" &&
            response.data !== null &&
            "components" in response.data
          ) {
            const headerComponent = (response.data as any).components.find(
              (component: any) =>
                component.type === "HEADER" && component.format === "IMAGE",
            );

            if (headerComponent) {
              imageUrl =
                headerComponent.example?.header_handle?.[0] ||
                headerComponent.example?.header_url?.[0] ||
                headerComponent.example?.header_url;
            }
          }
        } catch (error) {
          console.error(
            "❌ [TEMPLATE DEBUG] Erro ao carregar parâmetros do template:",
            error,
          );
        }
      }

      console.log("🖼️ [TEMPLATE DEBUG] URL da imagem do template:", {
        templateName: template.template_name,
        templateId: template.template_id,
        hasTemplateParameters: !!template.templateParameters,
        hasComponents: !!template.templateParameters?.components,
        imageUrl: imageUrl,
        headerComponent: template.templateParameters?.components?.find(
          (c: any) => c.type === "HEADER",
        ),
        fullTemplateParameters: template.templateParameters,
      });

      // Se não encontrou imageUrl, verificar se o template realmente tem header de imagem
      if (!imageUrl) {
        console.log("⚠️ [TEMPLATE DEBUG] Não foi possível obter imageUrl");

        // Verificar se o template realmente tem um componente HEADER com formato IMAGE
        const hasImageHeader = template.templateParameters?.components?.some(
          (c: any) => c.type === "HEADER" && c.format === "IMAGE",
        );

        if (hasImageHeader) {
          console.log(
            "🖼️ [TEMPLATE DEBUG] Template tem header de imagem, usando URL padrão",
          );
          imageUrl =
            "https://via.placeholder.com/800x600/008069/ffffff?text=Template+Image";
        } else {
          console.log(
            "📝 [TEMPLATE DEBUG] Template não tem header de imagem, não enviando imageUrl",
          );
          imageUrl = undefined;
        }
      }

      // Preparar parameters com os valores reais das variáveis
      let parameters: string[] = [];

      if (template.parameterConfigs && template.parameterConfigs.length > 0) {
        // Criar array de strings com os valores reais
        parameters = template.parameterConfigs.map((config) => config.value);
      }

      console.log("📝 [TEMPLATE DEBUG] Parâmetros para envio:", {
        templateName: template.template_name,
        templateId: template.template_id,
        parameters: parameters,
        parametersCount: parameters.length,
      });

      const result = await handleApiCall(
        async () => {
          return await sendTemplateMessage({
            to:
              selectedConversation.valid_telephone ||
              selectedConversation.telephone,
            templateId: template.template_id,
            templateName: template.template_name,
            templateLanguage: template.template_language,
            userId: account.id,
            companyId: account.company_id,
            imageUrl: imageUrl,
            parameters: parameters,
            // Vínculo com lead no CRM (para updates por id no webhook)
            leadId: (selectedConversation as any).id,
            campaignId: (selectedConversation as any).campaign_id,
            leadSource:
              (selectedConversation as any).lead_source || "leads_lead",
          } as any);
        },
        {
          onSuccess: (response) => {
            if (response.status === "success") {
              // Atualizar a interface do chat com a mensagem enviada
              onSendMessage(response.data.data);

              // Limpar o input após envio bem-sucedido
              setState((prev) => ({
                ...prev,
                inputValue: "",
              }));

              // Mostrar toast de sucesso
              showSuccess(
                `Template "${template.template_name}" enviado com sucesso!`,
              );
            } else {
              showError(response as IErrorResponse);
            }
            setSendingTemplate(false);
          },
          onError: () => {
            setSendingTemplate(false);
          },
          fallbackErrorMessage: "Erro ao enviar template. Tente novamente.",
          successMessage: undefined, // Não mostrar toast automático, já temos showSuccess
        },
      );

      if (!result) {
        setSendingTemplate(false);
      }
    },
    [
      selectedConversation,
      account,
      showError,
      showSuccess,
      handleApiCall,
      onSendMessage,
    ],
  );

  const emojiButtonRef = useRef<HTMLButtonElement>(null);
  const emojiPickerRef = useRef<HTMLDivElement>(null);

  const handleClickOutside = useCallback((event: MouseEvent) => {
    if (
      emojiButtonRef.current &&
      emojiPickerRef.current &&
      !emojiButtonRef.current.contains(event.target as Node) &&
      !emojiPickerRef.current.contains(event.target as Node)
    ) {
      setState((prev) => ({ ...prev, showEmojiPicker: false }));
    }
  }, []);

  useEffect(() => {
    if (state.showEmojiPicker) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [state.showEmojiPicker, handleClickOutside]);

  useEffect(() => {
    return () => {
      // Cleanup URLs de preview para evitar memory leaks
      state.previewUrls.forEach((url) => {
        if (url && url.startsWith("blob:")) {
          URL.revokeObjectURL(url);
        }
      });
    };
  }, [state.previewUrls]);

  if (loading) {
    return (
      <Container sx={{ px: "8px", py: "6px" }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: "8px",
            width: "100%",
            px: 1,
          }}
        >
          {/* Skeletons para botões da esquerda (Template e Emoji) */}
          <Skeleton
            animation="wave"
            variant="circular"
            width={32}
            height={32}
            sx={{ flexShrink: 0 }}
          />
          <Skeleton
            animation="wave"
            variant="circular"
            width={32}
            height={32}
            sx={{ flexShrink: 0 }}
          />

          {/* Skeleton para a caixa de input centralizada e branca */}
          <Box
            sx={{
              flex: 1,
              height: 40,
              bgcolor: "white",
              borderRadius: "8px",
              display: "flex",
              alignItems: "center",
              px: 2,
              mx: 0.5,
            }}
          >
            <Skeleton animation="wave" variant="text" width="60%" height={24} />
          </Box>

          {/* Skeletons para botões da direita (AI e Enviar/Mic) */}
          <Skeleton
            animation="wave"
            variant="circular"
            width={32}
            height={32}
            sx={{ flexShrink: 0 }}
          />
          <Skeleton
            animation="wave"
            variant="circular"
            width={32}
            height={32}
            sx={{ flexShrink: 0 }}
          />
        </Box>
      </Container>
    );
  }

  return (
    <Container>
      {showSlashMenu && filteredSlashTemplates.length > 0 && (
        <SlashMenuContainer>
          {filteredSlashTemplates.map((item, index) => {
            const template = item.templateDetails;
            const isSelected = index === selectedSlashIndex;
            return (
              <SlashMenuItem
                key={template.id}
                selected={isSelected}
                onClick={() => handleSelectSlashTemplate(item)}
              >
                <FormatListBulleted
                  style={{
                    color: isSelected ? "#008069" : "#54656f",
                    fontSize: 18,
                  }}
                />
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography
                    variant="subtitle2"
                    sx={{ fontWeight: 600, color: "#111b21" }}
                  >
                    {template.name}
                  </Typography>
                  {template.components?.find((c: any) => c.type === "BODY")
                    ?.text && (
                    <Typography
                      variant="caption"
                      sx={{
                        color: "#667781",
                        display: "block",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {
                        template.components.find((c: any) => c.type === "BODY")
                          .text
                      }
                    </Typography>
                  )}
                </Box>
              </SlashMenuItem>
            );
          })}
        </SlashMenuContainer>
      )}
      <Box sx={{ position: "relative" }}>
        {state.showEmojiPicker && (
          <EmojiPickerContainer ref={emojiPickerRef}>
            <EmojiPicker
              onEmojiClick={handleEmojiSelect}
              searchPlaceholder="Pesquisar emoji"
              width={350}
              height={400}
            />
          </EmojiPickerContainer>
        )}
      </Box>

      <InputContainer>
        {/* Template Button - Movido para antes do Emoji */}
        <Tooltip title="Enviar template">
          <span>
            <IconButtonStyled
              onClick={() => setShowTemplateSelector(true)}
              disabled={isInputDisabled || sendingTemplate}
              sx={{ padding: "4px" }}
            >
              <FormatListBulleted />
              {sendingTemplate && (
                <LoadingOverlay>
                  <CircularProgress size={16} />
                </LoadingOverlay>
              )}
            </IconButtonStyled>
          </span>
        </Tooltip>

        {/* Emoji Button */}
        <Tooltip title="Emojis">
          <span>
            <IconButtonStyled
              ref={emojiButtonRef}
              onClick={handleToggleEmojiPicker}
              disabled={isInputDisabled}
              sx={{
                padding: "4px",
                color: state.showEmojiPicker ? "#008069" : "#54656f",
              }}
            >
              <EmojiEmotions />
            </IconButtonStyled>
          </span>
        </Tooltip>

        {/* Text Input */}
        <StyledInput
          id="chat-message-input"
          name="message"
          inputProps={{
            "aria-multiline": "true",
            "aria-label": "Mensagem",
            "aria-disabled": isInputDisabled ? "true" : "false",
            role: "textbox",
          }}
          placeholder={
            isInputDisabled
              ? "Aguarde uma resposta do cliente ou inicie uma nova conversa"
              : "Digite uma mensagem"
          }
          value={state.inputValue}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          disabled={isInputDisabled}
          sx={{
            opacity: isInputDisabled ? 0.7 : 1,
            cursor: isInputDisabled ? "not-allowed" : "text",
          }}
        />

        {/* AI Agent Button */}
        <Tooltip
          title={
            aiState.isUpdating
              ? "Atualizando estado da IA..."
              : !isCompanyAIEnabled
                ? "IA geral da empresa está desativada"
                : aiState.isActive
                  ? "Desativar IA para esta conversa"
                  : "Ativar IA para esta conversa"
          }
        >
          <span>
            <IconButtonStyled
              onClick={handleToggleAI}
              data-is-updating={aiState.isUpdating}
              disabled={
                !selectedConversation || isInputDisabled || !isCompanyAIEnabled
              }
              sx={{
                padding: "4px",
                color: !isCompanyAIEnabled
                  ? "#9e9e9e"
                  : aiState.isActive
                    ? "#4caf50"
                    : "#f44336",
                "&:hover": {
                  backgroundColor: !isCompanyAIEnabled
                    ? "rgba(158, 158, 158, 0.1)"
                    : aiState.isActive
                      ? "rgba(76, 175, 80, 0.1)"
                      : "rgba(244, 67, 54, 0.1)",
                },
              }}
            >
              <SmartToy />
              {aiState.isUpdating && (
                <LoadingOverlay>
                  <CircularProgress size={16} />
                </LoadingOverlay>
              )}
            </IconButtonStyled>
          </span>
        </Tooltip>
      </InputContainer>

      {/* Audio Button */}
      <Tooltip title="Envio de áudio será implementado em breve">
        <span>
          <MicButton disabled={true}>
            <Mic />
          </MicButton>
        </span>
      </Tooltip>

      {/* Send Button */}
      {!isInputDisabled &&
        (state.inputValue.trim() || state.previewFiles.length > 0) && (
          <SendButton
            onClick={handleSendMessage}
            disabled={loading}
            data-has-text={!!state.inputValue.trim()}
          >
            {loading ? (
              <CircularProgress size={20} color="inherit" />
            ) : (
              <Send />
            )}
          </SendButton>
        )}

      <Menu
        anchorEl={state.fileAnchorEl}
        open={state.showFileMenu}
        onClose={() => handleFileMenu("close")}
        keepMounted
        disablePortal
        MenuListProps={{
          onKeyDown: (e) => {
            if (e.key === "Escape") {
              handleFileMenu("close");
            }
          },
        }}
      >
        <MenuItem onClick={() => handleFileMenu("select", "image/*")}>
          <ListItemIcon>
            <Image fontSize="small" />
          </ListItemIcon>
          <ListItemText>Imagem</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => handleFileMenu("select", "application/pdf")}>
          <ListItemIcon>
            <PictureAsPdf fontSize="small" />
          </ListItemIcon>
          <ListItemText>PDF</ListItemText>
        </MenuItem>
      </Menu>

      <FileInput
        type="file"
        ref={fileInputRef}
        onChange={handleFileChange}
        multiple
      />

      {state.previewFiles.length > 0 && (
        <PreviewContainer>
          {state.previewFiles.map((file, index) => (
            <PreviewItem key={`preview-${index}-${file.name}`}>
              <PreviewImage src={state.previewUrls[index]} alt={file.name} />
              <RemoveButton
                size="small"
                onClick={() => handleRemoveFile(index)}
              >
                ×
              </RemoveButton>
            </PreviewItem>
          ))}
        </PreviewContainer>
      )}

      {/* Template Selector */}
      {showTemplateSelector && (
        <TemplateSelector
          isOpen={showTemplateSelector}
          onClose={() => setShowTemplateSelector(false)}
          onSelectTemplate={handleTemplateSelect}
          campaignId={selectedConversation?.campaign_id}
        />
      )}
    </Container>
  );
});

ChatInput.displayName = "ChatInput";

export default ChatInput;
